/**
 * useVendorDirectory – React hook that loads and caches the vendor directory
 * for the current store. Provides resolveVendor(rawVendorName) → canonical
 * display name. Updates whenever the 'vendorDataChanged' event fires.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import supabase from '../config/supabase';
import {
  getCachedVendorDirectory,
  clearVendorDirectoryCache
} from './vendorResolution';

const REFRESH_INTERVAL_MS = 60_000;

export function useVendorDirectory() {
  const [directory, setDirectory] = useState(null);
  const storeIdRef = useRef(null);

  const refresh = useCallback(async (forceRefresh = false) => {
    const storedStoreId = localStorage.getItem('selectedStoreId');
    if (!storedStoreId) return;
    storeIdRef.current = storedStoreId;
    try {
      const dir = await getCachedVendorDirectory({
        supabaseClient: supabase,
        storeId: storedStoreId,
        forceRefresh,
        maxAgeMs: REFRESH_INTERVAL_MS
      });
      setDirectory(dir);
    } catch (err) {
      console.warn('[useVendorDirectory] Failed to load vendor directory:', err?.message);
    }
  }, []);

  useEffect(() => {
    refresh();

    const interval = setInterval(() => refresh(), REFRESH_INTERVAL_MS);

    const onVendorChanged = () => {
      const storeId = localStorage.getItem('selectedStoreId');
      if (storeId) clearVendorDirectoryCache(storeId);
      refresh(true);
    };

    document.addEventListener('vendorDataChanged', onVendorChanged);
    return () => {
      clearInterval(interval);
      document.removeEventListener('vendorDataChanged', onVendorChanged);
    };
  }, [refresh]);

  /**
   * Resolve a raw vendor string to its canonical display name.
   * Returns the canonical name if found, otherwise the original input.
   */
  const resolveVendor = useCallback(
    (rawVendorName) => {
      if (!rawVendorName || !directory) return rawVendorName || '';
      const { exactVendorByKey, aliasByKey } = directory;
      const key = normalizeKey(rawVendorName);
      if (!key) return rawVendorName;
      const vendorMatch = exactVendorByKey.get(key);
      if (vendorMatch) return vendorMatch.display_name;
      const aliasMatch = aliasByKey.get(key);
      if (aliasMatch?.vendor) return aliasMatch.vendor.display_name;
      return rawVendorName;
    },
    [directory]
  );

  /**
   * Resolve all unique vendor strings in an items array and return a
   * de-duplicated sorted list of canonical vendor names.
   */
  const resolveUniqueVendors = useCallback(
    (items = []) => {
      const seen = new Set();
      const resolved = [];
      (items || []).forEach((item) => {
        const raw = item.vendor || '';
        if (!raw) return;
        const canonical = resolveVendor(raw);
        if (!seen.has(canonical)) {
          seen.add(canonical);
          resolved.push(canonical);
        }
      });
      return resolved.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    },
    [resolveVendor]
  );

  return { directory, resolveVendor, resolveUniqueVendors };
}

function normalizeKey(value) {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\w\s]+/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Standalone (non-hook) resolver for use outside of React components.
 * Requires a vendor directory object already fetched via getCachedVendorDirectory.
 */
export function resolveVendorName(directory, rawVendorName) {
  if (!rawVendorName || !directory) return rawVendorName || '';
  const { exactVendorByKey, aliasByKey } = directory;
  const key = normalizeKey(rawVendorName);
  if (!key) return rawVendorName;
  const vendorMatch = exactVendorByKey.get(key);
  if (vendorMatch) return vendorMatch.display_name;
  const aliasMatch = aliasByKey.get(key);
  if (aliasMatch?.vendor) return aliasMatch.vendor.display_name;
  return rawVendorName;
}
